/**
 * Staging-only test fault switch for the call-quality instrument (#2224).
 *
 * Lets a tester cause real audio faults on demand so the outage measurement
 * and the "not heard" warning can be checked against something that actually
 * happened. It exists only when the server said `call_diagnostics: true`
 * (CALL_DIAGNOSTICS_ENABLED, off in production) AND the page URL has
 * `?fault=1`. Otherwise `window.__wecoFault` is never defined and nothing
 * here runs.
 *
 * Faults:
 *  - stopOutbound: detaches the mic from its RTP sender (replaceTrack(null)),
 *    so no audio leaves the device; no mute is signalled. Restored after.
 *  - blockSpeaker: unsubscribes one speaker's mic on this listener only.
 *  - fakeNothingSent: audio keeps flowing; only this device's own outbound
 *    measurement is told "nothing sent". The tracker's rules are untouched.
 * Overlapping calls of one fault extend it; leaving the call clears them all.
 */
import { Track } from "livekit-client";

/** What the fault code needs of a mic track's RTP sender. */
export interface SenderLike {
  replaceTrack(track: MediaStreamTrack | null): Promise<void>;
}
export interface MicTrackLike {
  sender?: SenderLike;
  mediaStreamTrack: MediaStreamTrack;
}
export interface RoomLike {
  localParticipant: {
    getTrackPublication(source: Track.Source): { track?: unknown } | undefined;
  };
  remoteParticipants: Map<
    string,
    { getTrackPublication(source: Track.Source): { setSubscribed(subscribed: boolean): void } | undefined }
  >;
}

export interface ActiveFault {
  name: FaultName;
  /** The speaker, for blockSpeaker. */
  target?: string;
  endsAt: number;
}
export type FaultName = "stopOutbound" | "blockSpeaker" | "fakeNothingSent";

export interface WecoFaultApi {
  stopOutbound(seconds: number): boolean;
  blockSpeaker(identity: string, seconds: number): boolean;
  fakeNothingSent(seconds: number): boolean;
  status(): ActiveFault[];
}

declare global {
  interface Window {
    __wecoFault?: WecoFaultApi;
  }
}

/** Faults may exist only when the server allows them and the URL asks for them. */
export function faultsAllowed(callDiagnostics: boolean, search: string): boolean {
  if (!callDiagnostics) return false;
  try {
    return new URLSearchParams(search).get("fault") === "1";
  } catch {
    return false;
  }
}

interface Running {
  fault: ActiveFault;
  timer: ReturnType<typeof setTimeout>;
  end: () => void;
}

export interface OutboundSample {
  packetsSent: number | null;
  bytesSent: number | null;
}

export class FaultController {
  private running = new Map<string, Running>();
  private frozen: OutboundSample | null = null;
  readonly api: WecoFaultApi;

  constructor(
    private readonly room: RoomLike,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.api = {
      stopOutbound: (s) => this.stopOutbound(s),
      blockSpeaker: (id, s) => this.blockSpeaker(id, s),
      fakeNothingSent: (s) => this.fakeNothingSent(s),
      status: () => this.status(),
    };
  }

  status(): ActiveFault[] {
    return Array.from(this.running.values()).map((r) => ({ ...r.fault }));
  }

  /**
   * The outbound counters the outage tracker should see. Real ones, except
   * while fakeNothingSent is on: then the values at the moment it began, so
   * the device reports zero packets leaving while audio really keeps flowing.
   */
  outboundSample(real: OutboundSample): OutboundSample {
    if (!this.running.has("fakeNothingSent")) {
      this.frozen = null;
      return real;
    }
    if (!this.frozen) this.frozen = { ...real };
    return this.frozen;
  }

  stopOutbound(seconds: number): boolean {
    const track = this.micTrack();
    if (!track?.sender) {
      this.log("stopOutbound", "skipped (no mic sender)");
      return false;
    }
    const sender = track.sender;
    return this.run("stopOutbound", undefined, seconds, () => {
      void sender.replaceTrack(null).catch(() => {});
    }, () => {
      // Resolve both again now: after a device switch or a full republish the
      // sender and track captured at start are stale. A new sender already
      // carries the new track; writing the current track to it is harmless.
      const current = this.micTrack();
      if (current?.sender) void current.sender.replaceTrack(current.mediaStreamTrack).catch(() => {});
    });
  }

  blockSpeaker(identity: string, seconds: number): boolean {
    const pub = this.room.remoteParticipants.get(identity)?.getTrackPublication(Track.Source.Microphone);
    if (!pub) {
      this.log("blockSpeaker", `skipped (no mic from ${identity})`);
      return false;
    }
    return this.run("blockSpeaker", identity, seconds, () => pub.setSubscribed(false), () => pub.setSubscribed(true));
  }

  fakeNothingSent(seconds: number): boolean {
    return this.run("fakeNothingSent", undefined, seconds, () => {
      this.frozen = null;
    }, () => {
      this.frozen = null;
    });
  }

  /** Ends every fault now and puts everything back. Called when the call ends. */
  dispose(): void {
    for (const key of Array.from(this.running.keys())) this.finish(key);
  }

  private run(name: FaultName, target: string | undefined, seconds: number, start: () => void, end: () => void): boolean {
    const ms = Math.max(0, seconds) * 1000;
    const key = target ? `${name}:${target}` : name;
    const endsAt = this.now() + ms;
    const existing = this.running.get(key);
    if (existing) {
      // Extend, never re-detach: the original track is still the one to restore.
      clearTimeout(existing.timer);
      existing.fault.endsAt = Math.max(existing.fault.endsAt, endsAt);
      existing.timer = setTimeout(() => this.finish(key), Math.max(0, existing.fault.endsAt - this.now()));
      this.log(name, `extend ${seconds}s`, target);
      return true;
    }
    start();
    this.running.set(key, { fault: { name, target, endsAt }, timer: setTimeout(() => this.finish(key), ms), end });
    this.log(name, `start ${seconds}s`, target);
    return true;
  }

  private finish(key: string): void {
    const r = this.running.get(key);
    if (!r) return;
    clearTimeout(r.timer);
    this.running.delete(key);
    try {
      r.end();
    } finally {
      this.log(r.fault.name, "end", r.fault.target);
    }
  }

  private micTrack(): MicTrackLike | undefined {
    return this.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as MicTrackLike | undefined;
  }

  private log(name: string, what: string, target?: string): void {
    console.info(`[weco-fault] ${name} ${what}${target ? ` ${target}` : ""}`, new Date().toISOString());
  }
}

/**
 * Installs `window.__wecoFault` when allowed. Returns the controller (for the
 * sampler's override) and a cleanup that restores everything, or null when
 * faults are not allowed — in which case nothing was touched.
 */
export function installFaults(
  room: RoomLike,
  callDiagnostics: boolean,
  search: string,
): { controller: FaultController; dispose: () => void } | null {
  if (typeof window === "undefined") return null;
  if (!faultsAllowed(callDiagnostics, search)) {
    // Tell a tester why ?fault=1 did nothing.
    if (!callDiagnostics && new URLSearchParams(search).get("fault") === "1") {
      console.info("[weco-fault] ?fault=1 ignored: call diagnostics off");
    }
    return null;
  }
  const controller = new FaultController(room);
  window.__wecoFault = controller.api;
  return {
    controller,
    dispose: () => {
      controller.dispose();
      if (window.__wecoFault === controller.api) delete window.__wecoFault;
    },
  };
}
